/** @format */

import React, { useState, useMemo, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import "./helpers/filtering.css";
import { ReactComponent as BagIcon } from "images/svg/bag.svg";
import { capitalize, truncateString } from "utils/strings";
import { ColumnFilter } from "./helpers/Columns/ColumnFilter";
import { FadeLoader } from "react-spinners";
import { tableEntries } from "utils/constant.data";

const AttendaceReportTable = ({
  messages,
  metadata,
  setFilter,
  setSkip,
  filtering,
  setSearch,
  setSearched,
  filterData,
  setModalFilter,
  setClear,
  setFiltered,
}) => {
  const [entriesPerPage, setEntriesPerPage] = useState(tableEntries[0]);

  useEffect(() => {
    setSkip(true);
  }, [messages, setSkip]);

  const BROADCAST_COLUMNS = [
    {
      Header: "Fellow ID",
      accessor: "fellow_id",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return `${capitalize(truncateString(cell?.row?.original?.fellow_id))}`;
      },
    },
    {
      Header: "Full Name",
      accessor: "full_name",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return (
          <div className="">
            <h6 className="mb-0">{`${capitalize(truncateString(cell?.row?.original?.full_name))}`}</h6>
            <p className="mb-0">{`${capitalize(truncateString(cell?.row?.original?.email))}`}</p>
          </div>
        );
      },
    },
    {
      Header: "Course",
      accessor: "course_name",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        const program = cell.row.original.current_program;
        return `${capitalize(
          truncateString(
            program === "3mtt"
              ? cell?.row?.original?.course_name
              : cell?.row?.original?.deeptech_course_name
          )
        )}`;
      },
    },
    {
      Header: "LGA",
      accessor: "lga",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return `${capitalize(truncateString(cell?.row?.original?.lga))}`;
      },
    },
    {
      Header: "Provider ID",
      accessor: "custom_provider_id",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return `${capitalize(
          truncateString(cell?.row?.original?.custom_provider_id)
        )}`;
      },
    },
    {
      Header: "Provider Name",
      accessor: "provider_name",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return `${capitalize(
          truncateString(cell?.row?.original?.provider_name)
        )}`;
      },
    },
    {
      Header: "Total Sessions",
      accessor: "total_sessions",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return cell?.row?.original?.total_sessions;
      },
    },
    {
      Header: "Overall Attendance(%)",
      accessor: "attendance_percentage",
      Filter: ColumnFilter,
      disableFilters: true,
      sortable: false,
      Cell: ({ cell }) => {
        return `${Math.ceil(cell?.row?.original?.attendance_percentage || 0)}%`;
      },
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => BROADCAST_COLUMNS, []);
  const data = useMemo(() => messages, [messages]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const restoreFellows = () => {
    setFiltered(false);
    setSearch("");
    setFilter(`page=1&page_size=${metadata?.page_size}`);
    setModalFilter(null);
    setSearched(false);
    setClear(!filterData?.clear);
  };

  const renderPaginationButtons = () => {
    const pageCount = Math.ceil(metadata?.count / metadata?.page_size);
    const totalPages = pageCount > 0 ? pageCount : 1;
    const lastPageIndex = (totalPages - 1) * metadata?.page_size;

    const buttons = [];

    // First page and previous buttons
    buttons.push(
      <button
        key="first"
        onClick={() => setFilter(`page=1&page_size=${metadata?.page_size}`)}
        className="pagination-button"
      >
        «
      </button>,
      <button
        key="prev"
        onClick={() =>
          setFilter(
            `page=${metadata?.page - 1}&page_size=${metadata?.page_size}`
          )
        }
        className={`pagination-button ${
          metadata?.page === 1 ? "disabled" : ""
        }`}
      >
        ‹
      </button>
    );

    // Page numbers
    for (let i = 1; i <= Math.min(8, totalPages); i++) {
      buttons.push(
        <button
          key={i}
          onClick={() =>
            setFilter(`page=${i}&page_size=${metadata?.page_size}`)
          }
          className={`pagination-button ${
            metadata?.page === i ? "active" : ""
          }`}
        >
          {i}
        </button>
      );
    }

    // Ellipsis and last page if needed
    if (totalPages > 8) {
      buttons.push(
        <span key="ellipsis" className="pagination-ellipsis">
          ...
        </span>,
        <button key="100" className="pagination-button">
          100
        </button>
      );
    }

    // Next and last page buttons
    buttons.push(
      <button
        key="next"
        onClick={() =>
          setFilter(
            `page=${metadata?.page + 1}&page_size=${metadata?.page_size}`
          )
        }
        className={`pagination-button ${
          metadata?.page === lastPageIndex + 1 ? "disabled" : ""
        }`}
      >
        ›
      </button>,
      <button
        key="last"
        onClick={() =>
          setFilter(
            `page=${lastPageIndex + 1}&page_size=${metadata?.page_size}`
          )
        }
        className="pagination-button"
      >
        »
      </button>
    );

    return buttons;
  };

  return (
    <>
      <div className="card">
        {filtering ? (
          <div className="d-flex justify-content-center align-items-center height-15 bg-white ">
            <FadeLoader color="#36d7b7" />
          </div>
        ) : (
          <div className={`card-body ${filtering ? "disabled" : ""}`}>
            {data?.length > 0 ? (
              <div className="table-responsive">
                <div className="dataTables_wrapper">
                  <table
                    {...getTableProps()}
                    className="table dataTable display"
                  >
                    <thead>
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column) => (
                            <th
                              {...column.getHeaderProps(
                                column.getSortByToggleProps()
                              )}
                            >
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {rows.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                              return (
                                <td {...cell.getCellProps()}>
                                  {" "}
                                  {cell.render("Cell")}{" "}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="pagination-container">
                    <div className="entries-selector">
                      <span>Show</span>
                      <select
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setFilter(`page=1&page_size=${e.target.value}`);
                        }}
                      >
                        {tableEntries?.map((entry) => (
                          <option value={entry}>{entry}</option>
                        ))}
                      </select>
                      <span>Entries</span>
                    </div>

                    <div className="pagination-buttons">
                      {renderPaginationButtons()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-center ai-icon  text-primary">
                  <BagIcon />
                  <h4 className="my-2">No fellows for the specified filter</h4>
                  <button
                    onClick={() => restoreFellows()}
                    className="btn btn-primary"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AttendaceReportTable;
